import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  AgxRow,
  AgxColumn,
  AgxHeader,
  AgxLabel,
  Images,
  AgxBodyText,
  AgxSlideUpModal,
  AgxMultiOptionButton,
  DocumentTypesMap,
  DocumentTypes,
  StakeholderType,
  SendConfiguration,
  AustralianState,
  MultiOptionButtonOption,
  FormConfigType,
  VendorBuyer,
  UploadedDocument,
} from '@urbanx/agx-ui-components';
import {
  AgxModal,
  AgxModalTitleIcon,
  AgxUploadedDocumentPdfEditor,
  AgxButton,
  AgxEmptyState,
  AgxSpacer,
  AgxAnimatedToggleLoaderOverlay,
  AgxAnimatedToggleLoaderState,
  AgxRadioGroup,
  Icon_DownloadOutline,
  Icon_AlertCircleOutline,
  generateEditedPdf,
} from '@urbanx/agx.ui-components2';
import type {
  AgxAnimatedToggleLoaderStateValue,
  PdfEditorSaveStatus,
  PdfEditorState,
} from '@urbanx/agx.ui-components2';
import { isEqual } from 'lodash-es';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useGetFileLink, useUploadFile } from 'components/ui-components/File/fileApi';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useFormClearer } from 'hooks/useFormCleaner';
import { useFormSettings } from 'hooks/useFormSettings';
import usePropertyImageSelector from 'selectors/usePropertyImageSelector';
import useFormDocumentsSelector from 'selectors/useFormDocumentsSelector';
import useFormId from 'hooks/useFormId';
import useFormCampaignId from 'hooks/useFormCampaignId';
import ErrorModal from 'helpers/ErrorModal';
import { Breakpoints, ScreenSize } from 'utils/screen';
import { markCampaignsAsStale } from '../../campaigns/campaignsReducer';
import { completeCampaignStep, saveFormCampaign, sendForm } from 'Api/Campaigns/campaignsApi';
import {
  loadFormConfig,
  saveContractExchangePdfAlterations as saveContractExchangePdfAlterationsInForm,
} from '../../form/formReducer';
import { setAndShowErrorToast } from 'store/config';
import { PreparedSignatory, prepareSignatories } from 'utils/vendorBuyerUtil';
import placeHolderImage from 'assets/images/placeholder-property.png';
import contractPreview from 'assets/images/contract-preview.png';
import SendConfirmation from './SendConfirmation';
import './ContractReviewAndSend.scss';
import { openFileInNewTab } from 'utils/openFileInNewTab';
import { sortDocumentsByType, sortDocumentsByTypes } from 'utils/documentUtil';
import { completeMarketingPackageStep, sendMarketingPackage } from 'Api/Marketing/marketingApi';
import { FormType } from '@urbanx/agx.campaigns.client';
import { buildContractExchangeInitialsTemplate } from './contractExchangeInitialsPdfTemplate';
import { fitContractExchangeMappingFields } from './contractExchangePdfEditState';
import { useCampaignStaleMarker } from 'hooks/useCampaignStaleMarker';
import { cleanStreetAddress } from 'utils/formatAddress';

interface ContractReviewAndSignProps {
  goToFirstPage: () => void;
  goToPreviousPage: () => void;
  vendors?: VendorBuyer[];
  formName?: string;
  sendViaDocuSign: boolean;
  disableFormSubmission?: boolean;
  sendConfigurations: SendConfiguration[];
}

const getUploadedDocumentKey = (document?: UploadedDocument) =>
  document?.documentId ?? document?.containerFilePath;

const ContractReviewAndSign = (props: ContractReviewAndSignProps) => {
  const {
    goToFirstPage,
    goToPreviousPage,
    vendors,
    formName,
    sendViaDocuSign,
    disableFormSubmission,
    sendConfigurations,
  } = props;
  const updateFormSettings = useFormSettings();
  const markCampaignAsStale = useCampaignStaleMarker();
  const formId = useFormId();

  const [showConfirmationModel, setShowConfirmationModel] = useState(false);
  const [showDownloadConfirmation, setShowDownloadConfirmation] = useState(false);
  const [includeSolicitorsInDownloadedSalesAdvice, setIncludeSolicitorsInDownloadedSalesAdvice] =
    useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [buyerSignatories, setBuyerSignatories] = useState<PreparedSignatory[]>([]);
  const [vendorSignatories, setVendorSignatories] = useState<PreparedSignatory[]>();
  const [docuSignLoaderState, setDocuSignLoaderState] =
    useState<AgxAnimatedToggleLoaderStateValue>();
  const isMobile = ScreenSize() === Breakpoints.Mobile;
  const isDesktop = ScreenSize() === Breakpoints.Desktop;
  const {
    formErrors: errors,
    downloadDocumentTypes,
    initialGeneratedFormDocuments,
  } = useAppSelector((state) => state.form);
  const availableForms = useAppSelector((state) => state.forms.availableForms);
  const { medium } = usePropertyImageSelector() || {};
  const documents = useFormDocumentsSelector();
  const getFileLink = useGetFileLink();
  const uploadFile = useUploadFile();
  const { getAuthToken } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const clearFormData = useFormClearer();
  const [showRecipients, setShowRecipients] = useState(false);
  const [configuration, setConfiguration] = useState(sendConfigurations[0]);
  const [disableButtons, setDisableButtons] = useState(false);
  const [showPdfEditor, setShowPdfEditor] = useState(false);
  const [pdfEditorSaveStatus, setPdfEditorSaveStatus] = useState<PdfEditorSaveStatus>('idle');
  const [isReplayingPdfAlterations, setIsReplayingPdfAlterations] = useState(false);
  const [pdfAlterationReplayError, setPdfAlterationReplayError] = useState<string>();
  const replayedInitialDocumentKeyRef = useRef<string | undefined>(undefined);
  const activeReplayKeyRef = useRef<string | undefined>(undefined);
  const isMountedRef = useRef(true);
  const emailDownloadedSalesAdviceRef = useRef<
    { formId: string; promise: Promise<void> } | undefined
  >(undefined);
  const [buyerSolicitorRecipient, setBuyerSolicitorRecipient] = useState<PreparedSignatory>();
  const [vendorSolicitorRecipient, setVendorSolicitorRecipient] = useState<PreparedSignatory>();

  const { selectedForm, formValues, pageStatuses, address } = useAppSelector((state) => state.form);
  const {
    Buyers: campaignBuyers,
    BuyerSolicitor: buyerSolicitor,
    VendorSolicitor: vendorSolicitor,
    ContractPdfAlterations: contractPdfAlterations,
    ContractPdfInitialDocument: contractPdfInitialDocument,
    ContractPdfAlteredDocument: contractPdfAlteredDocument,
  } = formValues;
  const campaignId = useFormCampaignId();
  const isContractExchange = selectedForm?.formType === FormType.ContractExchange;
  const isNswContractExchange = isContractExchange && selectedForm?.state === AustralianState.NSW;
  const sendingLoaderLabel = `Sending ${formName}`;
  const activeContractDocument = documents.find(
    (document) => document.documentType === DocumentTypes.ContractExchange
  );
  const originalContractDocument = initialGeneratedFormDocuments.find(
    (document) => document.documentType === DocumentTypes.ContractExchange
  );
  const alteredGeneratedContract = contractPdfAlteredDocument as UploadedDocument | undefined;
  const activeContractKey = getUploadedDocumentKey(activeContractDocument);
  const alteredContractKey = getUploadedDocumentKey(alteredGeneratedContract);
  const alteredContractMatchesActiveContract =
    Boolean(alteredContractKey) && alteredContractKey === activeContractKey;
  const fallbackInitialGeneratedContract = alteredContractMatchesActiveContract
    ? (originalContractDocument ?? activeContractDocument)
    : (activeContractDocument ?? originalContractDocument);
  const initialGeneratedContract =
    (contractPdfInitialDocument as UploadedDocument | undefined) ??
    fallbackInitialGeneratedContract;
  const generatedContract =
    alteredGeneratedContract ?? initialGeneratedContract ?? activeContractDocument;
  const generatedSalesAdvice = documents.find(
    (document) => document.documentType === DocumentTypes.SalesAdvice
  );
  const listingAddress = address ? cleanStreetAddress(address) : 'this property';

  const {
    agents: { loadingState: agentsLoaded, items: agents },
  } = useAppSelector((state) => state.agencies);

  const documentTypes = documents.map((doc) => doc.documentType);
  const uniqueTypes = new Set(documentTypes);

  const noAttachments = uniqueTypes.size === 2 && uniqueTypes.has(DocumentTypes.ContractExchange);

  const fileDownloadOptions = useMemo(() => {
    if (documents.length === 0) return [];
    const options: Array<MultiOptionButtonOption & Pick<UploadedDocument, 'containerFilePath'>> =
      [];
    const hasCustomDownloads = downloadDocumentTypes && downloadDocumentTypes.length > 0;

    const filteredDocuments = documents.filter(
      (document) => !hasCustomDownloads || downloadDocumentTypes.includes(document.documentType)
    );

    const orderedDocuments = !hasCustomDownloads
      ? sortDocumentsByType(filteredDocuments)
      : sortDocumentsByTypes(filteredDocuments, downloadDocumentTypes);

    orderedDocuments.forEach((d) => {
      options.push({
        text: d.documentType
          ? DocumentTypesMap[d.documentType as keyof typeof DocumentTypesMap]
          : '',
        containerFilePath: d.containerFilePath,
        onClick: async () => {
          const fileLink = await getFileLink(d.containerFilePath);
          if (fileLink != null) {
            openFileInNewTab(isDesktop, fileLink);
          }
        },
      });
    });

    return options;
  }, [documents, downloadDocumentTypes, getFileLink, isDesktop]);

  const contractExchangeFieldBlockTemplates = useMemo(() => {
    const initialsTemplate = buildContractExchangeInitialsTemplate({
      vendorSignatories,
      buyerSignatories,
    });

    return initialsTemplate ? [initialsTemplate] : [];
  }, [buyerSignatories, vendorSignatories]);

  const savedContractPdfEditState = fitContractExchangeMappingFields(
    contractPdfAlterations as PdfEditorState | undefined
  );
  const hasSavedContractPdfAlterations = Boolean(
    savedContractPdfEditState &&
    (savedContractPdfEditState.textBoxes?.length ||
      savedContractPdfEditState.strikeOuts?.length ||
      savedContractPdfEditState.fieldBlocks?.length ||
      savedContractPdfEditState.mappingFields?.length)
  );
  const shouldReplayPdfAlterations = Boolean(
    isNswContractExchange &&
    initialGeneratedContract &&
    savedContractPdfEditState &&
    hasSavedContractPdfAlterations &&
    campaignId &&
    formId &&
    !alteredContractMatchesActiveContract
  );
  const sendActionsDisabled =
    disableButtons || isReplayingPdfAlterations || Boolean(pdfAlterationReplayError);

  const beginSubmission = async () => {
    if (!getAuthToken || !campaignId || !formId) return;

    if (selectedForm?.formConfigType === FormConfigType.Campaign) {
      const contractSubmissionForm = availableForms.find(
        (form) =>
          form.type === FormType.ContractSubmission && isEqual(form.state, selectedForm?.state)
      );

      if (contractSubmissionForm == null) {
        return;
      }
    }

    try {
      setDisableButtons(true);

      const authToken = await getAuthToken();

      if (!authToken) {
        setDisableButtons(false);
        return;
      }

      const newForm =
        selectedForm?.formConfigType === FormConfigType.Campaign
          ? await completeCampaignStep(authToken, campaignId, true, formId)
          : await completeMarketingPackageStep(authToken, campaignId, formId, true);

      if (!newForm) {
        dispatch(setAndShowErrorToast('Failed to start Contract submission'));
        setDisableButtons(false);
        return;
      }

      dispatch(loadFormConfig(newForm));
    } catch (err) {
      dispatch(setAndShowErrorToast('Failed to start Contract submission'));
    }

    setDisableButtons(false);
  };

  const sendEnvelope = async (
    emailVendorNow: boolean,
    signingOrderGroups?: StakeholderType[],
    includeSolicitorsInSalesAdvice?: boolean
  ) => {
    if (!getAuthToken || !campaignId) return;
    if (isReplayingPdfAlterations) return;
    if (pdfAlterationReplayError) {
      dispatch(setAndShowErrorToast(pdfAlterationReplayError));
      return;
    }

    try {
      setDocuSignLoaderState(AgxAnimatedToggleLoaderState.Loading);
      updateFormSettings({
        showBreadcrumbs: false,
        displayTitleVisible: false,
      });

      const authToken = await getAuthToken();

      if (!authToken) {
        updateFormSettings({
          showBreadcrumbs: true,
          displayTitleVisible: true,
        });
        setDocuSignLoaderState(undefined);
        return;
      }

      const sendFormSubmission = {
        campaignId,
        formId: formId || '',
        formType: selectedForm?.formType as FormType,
        state: selectedForm?.state as AustralianState,
        digitalSignatureSubmission: {
          sendVendorsNow: emailVendorNow,
          signingOrderGroups: isContractExchange ? signingOrderGroups : undefined,
          includeSolicitorsInSalesAdvice: isContractExchange
            ? includeSolicitorsInSalesAdvice
            : undefined,
        },
        documents: isContractExchange && generatedContract ? [generatedContract] : undefined,
      };

      if (selectedForm?.formConfigType === FormConfigType.Marketing) {
        await sendMarketingPackage(authToken, sendFormSubmission);
      }

      if (selectedForm?.formConfigType === FormConfigType.Campaign) {
        await sendForm(authToken, sendFormSubmission);
        dispatch(markCampaignsAsStale());
        void markCampaignAsStale(campaignId).catch(console.error);
      }

      setDocuSignLoaderState(AgxAnimatedToggleLoaderState.Complete);
    } catch (err: any) {
      updateFormSettings({
        showBreadcrumbs: true,
        displayTitleVisible: true,
      });
      setDocuSignLoaderState(undefined);
      console.error(err);
      dispatch(setAndShowErrorToast(err.message));
    }
  };

  const downloadContractForSigning = async () => {
    const fileLink = await getFileLink(fileDownloadOptions[0]!.containerFilePath);
    if (!fileLink) return;

    if (
      includeSolicitorsInDownloadedSalesAdvice &&
      emailDownloadedSalesAdviceRef.current?.formId !== formId
    ) {
      if (!getAuthToken || !campaignId || !formId || !selectedForm) return;

      try {
        setDisableButtons(true);
        const authToken = await getAuthToken();
        if (!authToken) return;

        if (emailDownloadedSalesAdviceRef.current?.formId !== formId) {
          emailDownloadedSalesAdviceRef.current = {
            formId,
            promise: sendForm(authToken, {
              campaignId,
              formId,
              formType: selectedForm.formType,
              state: selectedForm.state,
              digitalSignatureSubmission: {
                sendVendorsNow: false,
                includeSolicitorsInSalesAdvice: true,
                sendForDigitalSignature: false,
              },
            }),
          };
        }

        await emailDownloadedSalesAdviceRef.current.promise;
      } catch (err: any) {
        emailDownloadedSalesAdviceRef.current = undefined;
        dispatch(setAndShowErrorToast(err.message));
        return;
      } finally {
        setDisableButtons(false);
      }
    }

    await openFileInNewTab(isDesktop, fileLink);
    setShowDownloadConfirmation(false);
  };

  const handleDocuSignLoaderComplete = () => {
    setDocuSignLoaderState(undefined);
    clearFormData();
    navigate('/');
  };

  useEffect(() => {
    if (vendors) {
      setVendorSignatories(prepareSignatories(vendors, StakeholderType.Vendor));
    }
  }, [agents, agentsLoaded, vendors]);

  useEffect(() => {
    if (campaignBuyers) {
      setBuyerSignatories(prepareSignatories(campaignBuyers, StakeholderType.Buyer));
    }
  }, [campaignBuyers]);

  useEffect(() => {
    if (buyerSolicitor) {
      setBuyerSolicitorRecipient({
        id: buyerSolicitor.solicitor?.solicitorName,
        name: buyerSolicitor.solicitor?.solicitorName,
        email: buyerSolicitor.solicitor?.solicitorEmail,
        type: StakeholderType.BuyerSolicitor,
      });
    }
  }, [buyerSolicitor]);

  useEffect(() => {
    if (vendorSolicitor) {
      setVendorSolicitorRecipient({
        id: vendorSolicitor.solicitor?.solicitorName,
        name: vendorSolicitor.solicitor?.solicitorName,
        email: vendorSolicitor.solicitor?.solicitorEmail,
        type: StakeholderType.VendorSolicitor,
      });
    }
  }, [vendorSolicitor]);

  const confirmationDialogBody = (
    <AgxColumn veryLargeGap extraClasses={'confirmationModalStyle'}>
      <Images.AlertCircleOutline />
      <AgxHeader size={3}>Starting a submission?</AgxHeader>
      <AgxBodyText medium>
        You will not be able to edit the {formName} once you continue.
      </AgxBodyText>
      <AgxBodyText medium>Continue to submission?</AgxBodyText>
      <div className={'confirmationButtonContainerStyle'}>
        <AgxButton
          id="startsubmission"
          size="large"
          variant="primary"
          text="Yes, start submission"
          onClick={beginSubmission}
          disabled={disableButtons}
        />
        <AgxButton
          id="cancel"
          variant="hollow"
          size="large"
          text="No, go back"
          onClick={() => setShowConfirmationModel(false)}
          disabled={disableButtons}
        />
      </div>
    </AgxColumn>
  );

  const downloadSalesAdviceButton = (text = 'Download the Sales Advice') =>
    generatedSalesAdvice ? (
      <AgxButton
        id="btn-download-sales-advice"
        text={text}
        variant="linkBrand"
        size="large"
        onClick={async () => {
          const fileLink = await getFileLink(generatedSalesAdvice.containerFilePath);
          if (fileLink != null) {
            openFileInNewTab(isDesktop, fileLink);
          }
        }}
        disabled={disableButtons || isReplayingPdfAlterations}
        rightIcon={<Icon_DownloadOutline height={20} width={20} color="currentColor" />}
      />
    ) : null;

  const viewEditContractButton = (variant: 'primary' | 'hollow' = 'hollow') =>
    generatedContract ? (
      <AgxButton
        id="btn-view-edit-contract"
        text="View and edit Contract"
        variant={variant}
        size="large"
        onClick={() => setShowPdfEditor(true)}
        disabled={disableButtons || isReplayingPdfAlterations}
      />
    ) : null;

  const viewPdfButton = () => {
    if (isContractExchange) {
      if (!generatedContract && !generatedSalesAdvice) return;

      return (
        <AgxSpacer direction="vertical" gap="spacing-2xl" contentAlignment="center">
          {viewEditContractButton()}
          {downloadSalesAdviceButton()}
        </AgxSpacer>
      );
    }

    if (fileDownloadOptions.length === 0) return;

    if (fileDownloadOptions.length === 1 || noAttachments) {
      return (
        <AgxButton
          id="btn-download"
          text="Download"
          variant="hollow"
          size="medium"
          onClick={fileDownloadOptions[0].onClick}
        />
      );
    } else {
      return (
        <AgxMultiOptionButton
          id="viewContractPdfs"
          text="Download"
          hollow
          medium
          options={fileDownloadOptions}
        />
      );
    }
  };

  const errorInfo = (
    <AgxRow centered veryLargeGap extraClasses={'toastStyle'} fill={isMobile}>
      <span>
        <Images.AlertCircle />
      </span>
      <AgxBodyText>
        {`Please complete required fields before ${sendViaDocuSign ? 'Docusigning' : 'sending'}`}
        {sendViaDocuSign && (
          <>
            <br />
            <AgxButton
              id="btn-view-details"
              text="View Details"
              variant="link"
              onClick={() => setOpenErrorModal(true)}
            />
          </>
        )}
      </AgxBodyText>
    </AgxRow>
  );

  const onPreviousClicked = () => {
    goToPreviousPage();
  };

  const isPdfEditorSaving = pdfEditorSaveStatus === 'saving';
  const closePdfEditor = () => setShowPdfEditor(false);
  const cancelPdfEditor = () => {
    if (isPdfEditorSaving) return;
    setShowPdfEditor(false);
  };
  const setPdfEditorOpen = (open: boolean) => {
    if (!open && isPdfEditorSaving) return;
    setShowPdfEditor(open);
  };

  const getGeneratedContractFileLink = async (filePath: string) =>
    (await getFileLink(filePath)) ?? undefined;

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const persistAlteredContractPdf = async ({
    alteredUploadedDocument,
    editState,
  }: {
    alteredUploadedDocument: UploadedDocument;
    editState: PdfEditorState;
  }) => {
    if (!campaignId || !formId || !selectedForm || !initialGeneratedContract) {
      throw new Error('Unable to save edited contract.');
    }

    const authToken = await getAuthToken();

    if (!authToken) {
      throw new Error('Failed to login');
    }

    const nextFormValues = {
      ...formValues,
      ContractPdfAlterations: editState,
      ContractPdfInitialDocument: initialGeneratedContract,
      ContractPdfAlteredDocument: alteredUploadedDocument,
    };

    const formSaved = await saveFormCampaign(
      authToken,
      campaignId,
      formId,
      selectedForm.formType,
      selectedForm.state,
      nextFormValues,
      pageStatuses
    );

    if (!formSaved) {
      throw new Error('Unable to save edited contract.');
    }

    dispatch(
      saveContractExchangePdfAlterationsInForm({
        campaignId,
        formId,
        uploadedDocument: initialGeneratedContract,
        alteredUploadedDocument,
        editState,
      })
    );
  };

  const uploadEditedContractPdf = async ({
    file,
    editState,
  }: {
    file: File;
    editState: PdfEditorState;
  }) => {
    if (!campaignId || !formId || !initialGeneratedContract?.containerFilePath) {
      throw new Error('Unable to save edited contract.');
    }

    const authToken = await getAuthToken();

    if (!authToken) {
      throw new Error('Failed to login');
    }

    const formData = new FormData();
    formData.append('File', file);

    const uploadedDocument = (await uploadFile(
      authToken,
      formData,
      campaignId,
      DocumentTypes.ContractExchange
    )) as UploadedDocument | undefined;

    if (!uploadedDocument) {
      throw new Error('Unable to save edited contract.');
    }

    await persistAlteredContractPdf({
      alteredUploadedDocument: uploadedDocument,
      editState,
    });

    return uploadedDocument;
  };

  const handlePdfEditorError = (error: unknown) => {
    const message = error instanceof Error ? error.message : 'Unable to save edited contract.';
    dispatch(setAndShowErrorToast(message));
  };

  useEffect(() => {
    if (!shouldReplayPdfAlterations || !initialGeneratedContract || !savedContractPdfEditState) {
      return;
    }

    if (
      savedContractPdfEditState.fieldBlocks?.length &&
      contractExchangeFieldBlockTemplates.length === 0
    ) {
      return;
    }

    const replayKey = `${
      getUploadedDocumentKey(initialGeneratedContract) ?? 'missing-document'
    }:${JSON.stringify(savedContractPdfEditState)}`;

    if (replayedInitialDocumentKeyRef.current === replayKey) {
      return;
    }

    replayedInitialDocumentKeyRef.current = replayKey;
    activeReplayKeyRef.current = replayKey;

    const isCurrentReplay = () => isMountedRef.current && activeReplayKeyRef.current === replayKey;

    const replayPdfAlterations = async () => {
      try {
        setPdfAlterationReplayError(undefined);
        setIsReplayingPdfAlterations(true);

        const fileLink = await getGeneratedContractFileLink(
          initialGeneratedContract.containerFilePath
        );

        if (!fileLink) {
          throw new Error('Unable to load generated contract.');
        }

        const response = await fetch(fileLink);

        if (!response.ok) {
          throw new Error('Unable to load generated contract.');
        }

        const pdfBytes = new Uint8Array(await response.arrayBuffer());
        const editedPdf = await generateEditedPdf({
          pdfBytes,
          editState: savedContractPdfEditState,
          fieldBlockTemplates: contractExchangeFieldBlockTemplates,
        });
        const pdfArrayBuffer = editedPdf.buffer.slice(
          editedPdf.byteOffset,
          editedPdf.byteOffset + editedPdf.byteLength
        ) as ArrayBuffer;
        const file = new File([pdfArrayBuffer], 'Contract - edited.pdf', {
          type: 'application/pdf',
        });
        const uploadedDocument = await uploadEditedContractPdf({
          file,
          editState: savedContractPdfEditState,
        });

        if (isCurrentReplay() && uploadedDocument) {
          setPdfAlterationReplayError(undefined);
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Unable to apply saved contract edits.';

        if (isCurrentReplay()) {
          setPdfAlterationReplayError(message);
          dispatch(setAndShowErrorToast(message));
        }
      } finally {
        if (isCurrentReplay()) {
          setIsReplayingPdfAlterations(false);
          activeReplayKeyRef.current = undefined;
        }
      }
    };

    void replayPdfAlterations();
  }, [
    campaignId,
    contractExchangeFieldBlockTemplates,
    dispatch,
    formId,
    initialGeneratedContract,
    savedContractPdfEditState,
    shouldReplayPdfAlterations,
  ]);

  if (docuSignLoaderState)
    return (
      <AgxAnimatedToggleLoaderOverlay
        label={
          docuSignLoaderState === AgxAnimatedToggleLoaderState.Complete
            ? 'Sent'
            : sendingLoaderLabel
        }
        onComplete={handleDocuSignLoaderComplete}
        state={docuSignLoaderState}
      />
    );

  if (isReplayingPdfAlterations)
    return (
      <AgxColumn extraClasses="reviewAndSignImageCard">
        <AgxColumn veryLargeGap extraLargePadding centered extraClasses="agreementDetails">
          <AgxEmptyState variant="minimal" label="loading..." />
        </AgxColumn>
      </AgxColumn>
    );

  if (showRecipients)
    return (
      <SendConfirmation
        buyers={buyerSignatories}
        buyerSolicitor={buyerSolicitorRecipient}
        vendors={vendorSignatories}
        vendorSolicitor={vendorSolicitorRecipient}
        onBack={() => setShowRecipients(false)}
        onSend={(sendVendorsNow, signingOrderGroups, includeSolicitorsInSalesAdvice) =>
          sendEnvelope(sendVendorsNow, signingOrderGroups, includeSolicitorsInSalesAdvice)
        }
        sendViaDocuSign={sendViaDocuSign}
        sendConfiguration={configuration}
        isMobile={isMobile}
        enableSigningOrderSelection={isContractExchange}
        enableSalesAdviceSolicitorSelection={isContractExchange}
      />
    );

  return (
    <AgxColumn extraClasses="reviewAndSignImageCard">
      <AgxColumn veryLargeGap extraLargePadding centered extraClasses="agreementDetails">
        {isNswContractExchange && (generatedContract || generatedSalesAdvice) ? (
          <AgxSpacer
            direction="vertical"
            gap="spacing-2xl"
            contentAlignment="center"
            className="contractExchangePreviewActions"
          >
            {generatedContract && (
              <div className="contractExchangePreview">
                <img
                  className="contractExchangePreviewImage"
                  alt="Contract preview"
                  src={contractPreview}
                />
                <div className="contractExchangePreviewOverlay">
                  {viewEditContractButton('primary')}
                </div>
              </div>
            )}
            {downloadSalesAdviceButton('Download Sales Advice')}
          </AgxSpacer>
        ) : sendViaDocuSign ? (
          <img className="propertyImage" alt="Avatar" src={medium || placeHolderImage} />
        ) : (
          <Images.SparkleForm />
        )}
        {!isNswContractExchange && (
          <AgxRow largeGap centered justifyCenter={isMobile} fill={isMobile}>
            {viewPdfButton()}
            {!isContractExchange && (
              <AgxButton
                id="btn-edit"
                text="Edit"
                onClick={goToFirstPage}
                variant="hollow"
                size="medium"
                disabled={disableButtons}
              />
            )}
          </AgxRow>
        )}
      </AgxColumn>
      {sendViaDocuSign || sendConfigurations?.length > 1 ? (
        <div className="signingGrid">
          <AgxColumn largeGap centered extraClasses="signingGridCell">
            <AgxLabel large>Docusign</AgxLabel>
            {sendConfigurations.map((config, index) => (
              <AgxButton
                id={`btn-${config.title}-${index}`}
                key={`btn-${config.title}-${index}`}
                variant="primary"
                size="large"
                text={config.title}
                onClick={() => {
                  if (errors.length > 0) {
                    if (sendViaDocuSign) {
                      setOpenErrorModal(true);
                    }
                  } else {
                    setShowRecipients(true);
                    setConfiguration(config);
                  }
                }}
                disabled={sendActionsDisabled}
              />
            ))}
            {errors.length > 0 && errorInfo}
          </AgxColumn>
          {!disableFormSubmission && (
            <AgxColumn largeGap centered extraClasses="signingGridCell signingGridActions">
              <AgxLabel large>Sign in person</AgxLabel>
              <AgxButton
                id="btn-download-to-sign"
                variant="primary"
                size="large"
                text="Download to Sign"
                onClick={
                  isNswContractExchange
                    ? () => setShowDownloadConfirmation(true)
                    : fileDownloadOptions.length > 0
                      ? fileDownloadOptions[0].onClick
                      : undefined
                }
                wrapperClassName="signingGridAction"
                disabled={!fileDownloadOptions.length || sendActionsDisabled}
              />
              {fileDownloadOptions.length > 0 && (
                <AgxButton
                  id="btn-submit-signed-pdf"
                  variant="hollow"
                  size="large"
                  text="Submit Signed PDF"
                  onClick={() => setShowConfirmationModel(true)}
                  disabled={sendActionsDisabled}
                  wrapperClassName="signingGridAction"
                />
              )}
            </AgxColumn>
          )}
        </div>
      ) : (
        <>
          {errors.length > 0 && <AgxColumn extraClasses={'alignColumn'}>{errorInfo}</AgxColumn>}
          <div className="emailGrid borderTop">
            <AgxColumn centered>
              <AgxRow largeGap fill justifyCenter>
                <AgxButton
                  id="btn-review-back"
                  variant="hollow"
                  size="large"
                  text="Back"
                  onClick={onPreviousClicked}
                />
                <AgxButton
                  id="btn-review-next"
                  variant="primary"
                  size="large"
                  text="Next"
                  onClick={() => {
                    setShowRecipients(true);
                    setConfiguration(sendConfigurations[0]);
                  }}
                  disabled={errors.length > 0 || sendActionsDisabled}
                />
              </AgxRow>
            </AgxColumn>
          </div>
        </>
      )}

      {openErrorModal && <ErrorModal errorList={errors} setShowModal={setOpenErrorModal} />}

      {showConfirmationModel && (
        <AgxSlideUpModal
          closeButton
          content={confirmationDialogBody}
          desktop={!isMobile}
          onClose={() => setShowConfirmationModel(false)}
        />
      )}

      {showDownloadConfirmation && (
        <AgxModal
          id="download-to-sign-confirmation-modal"
          className="downloadToSignConfirmation"
          title="Do you want to email both solicitors a copy of the Sales Advice?"
          titleIcon={
            <AgxModalTitleIcon tone="warning">
              <Icon_AlertCircleOutline type="Warning" height={40} width={40} />
            </AgxModalTitleIcon>
          }
          type="single"
          variant="withoutDivider"
          actionButtonContainerStyle="withDivider"
          setIsDialogOpen={setShowDownloadConfirmation}
          content={
            <AgxSpacer padding="md">
              <AgxRadioGroup
                id="email-solicitors-sales-advice"
                label=""
                layout="vertical"
                value={includeSolicitorsInDownloadedSalesAdvice ? 'yes' : 'no'}
                options={[
                  { label: 'No', value: 'no' },
                  { label: 'Yes', value: 'yes' },
                ]}
                onChange={(value) => setIncludeSolicitorsInDownloadedSalesAdvice(value === 'yes')}
              />
            </AgxSpacer>
          }
          actionButtons={
            <AgxSpacer direction="horizontal" gap="spacing-lg" contentJustification="center" fill>
              <AgxButton
                id="download-to-sign-back"
                variant="hollow"
                size="large"
                text="Back"
                onClick={() => setShowDownloadConfirmation(false)}
                disabled={disableButtons}
                stretch
              />
              <AgxButton
                id="download-to-sign-confirm"
                variant="primary"
                size="large"
                text="Confirm"
                onClick={downloadContractForSigning}
                disabled={disableButtons}
                stretch
              />
            </AgxSpacer>
          }
        />
      )}

      {initialGeneratedContract && (
        <AgxModal
          id="contract-pdf-editor-modal"
          title={`Editing Contract for ${listingAddress}`}
          type="fullScreen"
          variant="withDivider"
          dividerClassName="contractPdfEditorDivider"
          actionButtonContainerStyle="withoutDivider"
          isOpen={showPdfEditor}
          setIsDialogOpen={setPdfEditorOpen}
          content={
            <AgxUploadedDocumentPdfEditor
              id="contract-exchange-pdf-editor"
              uploadedDocument={initialGeneratedContract}
              pdfFileName={initialGeneratedContract.fileName ?? 'Contract.pdf'}
              getFileLink={getGeneratedContractFileLink}
              initialEditState={savedContractPdfEditState}
              uploadEditedPdf={uploadEditedContractPdf}
              onCancel={cancelPdfEditor}
              onSaveStatusChange={setPdfEditorSaveStatus}
              onSaved={closePdfEditor}
              onError={handlePdfEditorError}
              showAddText
              showStrikeOut
              fieldBlockTemplates={contractExchangeFieldBlockTemplates}
              showActionButtons
              cancelButtonText="Cancel"
              saveButtonText="Save"
            />
          }
        />
      )}
    </AgxColumn>
  );
};

export default ContractReviewAndSign;
